import React, { useCallback, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import imageCompression from 'browser-image-compression'
import {
  fetchCurrentStorageSize,
  uploadImages,
} from '../../../services/gallery'
import { useTranslation } from 'react-i18next'
import { Upload } from 'lucide-react'
import CookieManager from '../../lib/CookiesManager'

interface UploadProgress {
  fileName: string
  progress: number
  status: 'uploading' | 'success' | 'error'
  originalSize?: number
  compressedSize?: number
}

interface UploadZoneProps {
  onImagesUploaded: (images: any[]) => void
  onStorageCheck: (newSize: number) => boolean
}

const compressionOptions = {
  maxSizeMB: 1,
  maxWidthOrHeight: 1920,
  useWebWorker: true,
  initialQuality: 0.7,
  fileType: 'image/jpeg',
}

// 🔥 Convertir PNG/GIF en JPEG via canvas
const convertToJpeg = async (file: File): Promise<File> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = event => {
      const img = new Image()
      img.onload = () => {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        if (!ctx) return reject(new Error('Canvas context error'))

        canvas.width = img.width
        canvas.height = img.height
        ctx.drawImage(img, 0, 0)

        canvas.toBlob(
          blob => {
            if (blob) {
              const newFile = new File(
                [blob],
                file.name.replace(/\.(png|gif)$/, '.jpg'),
                {
                  type: 'image/jpeg',
                },
              )
              resolve(newFile)
            } else {
              reject(new Error('Failed to convert to JPEG'))
            }
          },
          'image/jpeg',
          0.7,
        )
      }
      img.src = event.target?.result as string
    }
    reader.readAsDataURL(file)
  })
}

const cleanFileName = (originalName: string): string => {
  const nameWithoutExt = originalName.substring(
    0,
    originalName.lastIndexOf('.'),
  )
  const extension = originalName.slice(originalName.lastIndexOf('.'))

  const cleanedName = nameWithoutExt
    .toLowerCase()
    .replace(/\s+/g, '-')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')

  return `${cleanedName}-${Date.now()}${extension}`
}

const UploadZone: React.FC<UploadZoneProps> = ({
  onImagesUploaded,
  onStorageCheck,
}) => {
  const { t } = useTranslation()
  const [uploadProgress, setUploadProgress] = useState<UploadProgress[]>([])
  const [isProcessing, setIsProcessing] = useState(false)

  const compressImage = async (file: File): Promise<File> => {
    try {
      if (file.type === 'image/png' || file.type === 'image/gif') {
        file = await convertToJpeg(file)
      }

      const compressedFile = await imageCompression(file, compressionOptions)

      return compressedFile.size < file.size ? compressedFile : file
    } catch (error) {
      console.error('Image processing failed:', error)
      return file
    }
  }

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const userId = CookieManager.getUserId()
      const token = CookieManager.getToken()

      if (!userId || !token) return

      setIsProcessing(true)
      let availableSpace = 10 - (await fetchCurrentStorageSize(userId, token))
      let uploadableFiles: { file: File; originalSize: number }[] = []
      let cumulativeSize = 0

      try {
        const compressedFiles = await Promise.all(
          acceptedFiles.map(async file => {
            const compressedFile = await compressImage(file)
            return {
              file: compressedFile,
              originalSize: file.size,
            }
          }),
        )

        const sortedFiles = compressedFiles.sort(
          (a, b) => a.file.size - b.file.size,
        )

        for (const { file, originalSize } of sortedFiles) {
          const fileSize = file.size / (1024 * 1024)
          if (cumulativeSize + fileSize <= availableSpace) {
            uploadableFiles.push({ file, originalSize })
            cumulativeSize += fileSize
          }
        }

        for (const { file, originalSize } of uploadableFiles) {
          const cleanedFileName = cleanFileName(file.name).replace(
            /\.(png|gif)$/,
            '.jpg',
          )

          setUploadProgress(prev => [
            ...prev,
            {
              fileName: cleanedFileName,
              progress: 0,
              status: 'uploading',
              originalSize,
              compressedSize: file.size,
            },
          ])

          try {
            const parsedUserId = parseInt(userId, 10)
            if (isNaN(parsedUserId)) throw new Error('Invalid user ID')

            const renamedFile = new File([file], cleanedFileName, {
              type: file.type,
            })

            const uploadedImages = await uploadImages(
              parsedUserId.toString(),
              token,
              [renamedFile],
            )

            setUploadProgress(prev =>
              prev.map(p =>
                p.fileName === cleanedFileName
                  ? { ...p, progress: 100, status: 'success' }
                  : p,
              ),
            )

            onImagesUploaded(uploadedImages)
          } catch (error) {
            setUploadProgress(prev =>
              prev.map(p =>
                p.fileName === cleanedFileName ? { ...p, status: 'error' } : p,
              ),
            )
            console.error('Upload failed:', error)
          }
        }

        if (uploadableFiles.length < acceptedFiles.length) {
          onStorageCheck(availableSpace + 0.1)
        }
      } finally {
        setIsProcessing(false)

        setTimeout(() => {
          setUploadProgress([])
        }, 3000)
      }
    },
    [onImagesUploaded, onStorageCheck],
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': [],
      'image/png': [],
      'image/gif': [],
    },
    multiple: true,
    disabled: isProcessing,
  })

  return (
    <div
      {...getRootProps()}
      className='relative aspect-square border-2 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer transition-colors duration-200'
    >
      <input {...getInputProps()} />
      <Upload className='h-10 w-10 text-whatsapp' />
      <p className='text-sm font-medium text-gray-700'>{t('upload-images')}</p>
    </div>
  )
}

export default UploadZone
